/**
 * Bio Generator - Creates personalized bios for friend-finding (platonic only)
 * Templates are designed to be friendly, authentic, and non-flirtatious
 */

export type PersonalityType =
  | 'adventurer'
  | 'social'
  | 'creative'
  | 'chill'
  | 'active'
  | 'community';

interface PersonalityTemplate {
  id: PersonalityType;
  label: string;
  icon: string;
  description: string;
  template: (params: BioParams) => string;
}

interface BioParams {
  age?: number;
  interests: string[];
}

const PERSONALITY_TEMPLATES: PersonalityTemplate[] = [
  {
    id: 'adventurer',
    label: '🏕️ Bold & Outdoorsy',
    icon: '🏕️',
    description: 'Always seeking new experiences',
    template: ({ age, interests }) => {
      const intros = [
        'Always up for an adventure',
        'Big on exploring and trying new things',
        'Love getting out there and trying new stuff',
        'Life\'s too short to stay inside',
        'Can\'t resist a good adventure',
        'Happiest when I\'m exploring something new',
        'Thrive on spontaneity and new experiences',
        'Always chasing the next experience',
      ];

      const interestList = formatInterests(interests, 3);
      const middles = [
        `Really into ${interestList}`,
        `${interestList} are my jam`,
        `Big fan of ${interestList}`,
        `You'll usually find me doing ${interestList}`,
        `Passionate about ${interestList}`,
        `Can't get enough of ${interestList}`,
        `${interestList} get me buzzing`,
      ];

      const middles2 = [
        'but honestly open to most things',
        'and always keen to discover new spots',
        'though I\'m game for whatever sounds fun',
        'but I never say no to something new',
        'and I\'m always looking for the next thrill',
        'though my calendar\'s pretty flexible',
        'but willing to try pretty much anything once',
      ];

      const endings = [
        'Down to meet people who are up for adventures and good Vybes',
        'Looking for people to explore with and create some memories',
        'Let\'s go on some adventures together and see where it takes us',
        'Hit me up if you fancy getting out and doing something wild',
        'Looking for adventure buddies who keep things interesting',
        'Want to meet people who push me out of my comfort zone',
        'Keen to find my crew for weekend escapades',
      ];

      return `${random(intros)}. ${random(middles)}, ${random(middles2)}. ${random(endings)}!`;
    }
  },
  {
    id: 'social',
    label: '💬 Friendly & Social',
    icon: '💬',
    description: 'Building genuine friendships',
    template: ({ age, interests }) => {
      const intros = [
        'Looking to meet some cool people',
        'Building my friend circle',
        'Looking for my people',
        'Always up for making new connections',
        'Keen to expand my social circle',
        'Here to meet genuine, interesting people',
        'New friendships are what I\'m after',
        'Looking to vibe with like-minded folks',
      ];

      const interestList = formatInterests(interests, 3);
      const middles = [
        `Love ${interestList}`,
        `Into ${interestList}`,
        `${interestList} are my Vybe`,
        `Big on ${interestList}`,
        `${interestList} keep me going`,
        `You'll catch me at ${interestList}`,
        `Obsessed with ${interestList}`,
      ];

      const middles2 = [
        'and honestly just enjoy meeting interesting people',
        'plus whatever else sounds good',
        'but I\'m super open to new experiences',
        'though I\'m game for most things',
        'and love discovering new interests through friends',
        'but always curious about what others are into',
        'though my favourite hobby is good conversation',
      ];

      const endings = [
        'Always down for coffee, trying something new, or just having a good chat',
        'Let\'s hang out and see what happens - no expectations, just Vybes',
        'Hit me up if you want to grab coffee, explore, or just see if we click',
        'Fancy meeting up and seeing if we get on?',
        'Would love to connect over a drink or activity sometime',
        'Let\'s meet up, swap stories, and see where things go',
        'Looking forward to meeting you and seeing what we have in common',
      ];

      return `${random(intros)}! ${random(middles)}, ${random(middles2)}. ${random(endings)}!`;
    }
  },
  {
    id: 'creative',
    label: '🎨 Arty & Expressive',
    icon: '🎨',
    description: 'Passionate about arts and ideas',
    template: ({ age, interests }) => {
      const intros = [
        'Creative type looking for like-minded friends',
        'Always got a project going, looking for creative buddies',
        'Arty soul seeking fellow creatives',
        'Maker at heart, looking for creative connections',
        'Imagination runs wild - looking for people who get it',
        'Creative mind seeking inspiration through friendship',
        'Living for creative expression and meaningful conversations',
        'Artist looking to connect with other free spirits',
      ];

      const interestList = formatInterests(interests, 3);
      const middles = [
        `Super into ${interestList}`,
        `${interestList} are my thing`,
        `Love ${interestList}`,
        `${interestList} fuel my creativity`,
        `Spend most of my time on ${interestList}`,
        `Can\'t live without ${interestList}`,
        `${interestList} are where I find my flow`,
      ];

      const middles2 = [
        'and always looking for creative energy',
        'plus I appreciate good conversation',
        'but also down for chill hangs',
        'though I\'m equally happy just bouncing ideas around',
        'and love meeting people with different perspectives',
        'but always keen to explore other creative outlets too',
        'plus I thrive on deep conversations about anything',
      ];

      const endings = [
        'Down to collab, share ideas, or just chill and Vybe',
        'Would be cool to meet others who get it and see what we can create together',
        'Looking for people to create with, hang with, and build something meaningful',
        'Let\'s inspire each other and see what we make happen',
        'Keen to meet fellow creatives for projects, coffee, or whatever',
        'Want to connect with people who see the world a bit differently',
        'Looking for creative minds to collaborate and grow with',
      ];

      return `${random(intros)}. ${random(middles)}, ${random(middles2)}. ${random(endings)}!`;
    }
  },
  {
    id: 'chill',
    label: '☁️ Laid-Back & Easygoing',
    icon: '☁️',
    description: 'Easy-going and authentic',
    template: ({ age, interests }) => {
      const intros = [
        'Pretty laid back person',
        'Easy-going type',
        'Not trying to force anything, just looking for genuine friends',
        'Low-key vibes, just seeing who I connect with',
        'Relaxed approach to life and friendships',
        'Taking it easy and seeing where friendships naturally form',
        'No pressure - just here for authentic connections',
        'Chill personality looking for the same energy',
      ];

      const interestList = formatInterests(interests, 3);
      const middles = [
        `Enjoy ${interestList}`,
        `Like ${interestList}`,
        `${interestList} are cool with me`,
        `${interestList} help me unwind`,
        `Usually found doing ${interestList}`,
        `${interestList} are my go-to`,
        `Into ${interestList} when the mood strikes`,
      ];

      const middles2 = [
        'but also enjoy getting out when the mood strikes',
        'though I\'m always open to trying new things',
        'and appreciate quality over quantity in friendships',
        'but flexible and open to whatever',
        'though I like keeping things spontaneous',
        'and believe the best moments are unplanned',
        'but honestly just go with the flow',
      ];

      const endings = [
        'Always up for low-key hangouts, good conversation, or just seeing where things go',
        'Down for whatever - coffee, walks, or just hanging. No pressure, no drama',
        'Let\'s just see if we Vybe and take it from there. Genuine connections only',
        'Up for meeting without any expectations - just seeing if we click',
        'Looking for people who keep it real and easy',
        'Would be nice to find some chill people to hang with',
        'Let\'s grab a coffee or do something low-key and see how it goes',
      ];

      return `${random(intros)}. ${random(middles)}, ${random(middles2)}. ${random(endings)}!`;
    }
  },
  {
    id: 'active',
    label: '⚡ Active & Up for Anything',
    icon: '⚡',
    description: 'Always on the move',
    template: ({ age, interests }) => {
      const interestList = formatInterests(interests, 3);

      const intros = [
        'Looking for people to do stuff with',
        'Always got something going on',
        'Pretty active, looking for friends who are too',
        'High energy person who hates sitting still',
        'Can\'t stop moving - need friends who can keep up',
        'Always on the go and looking for activity partners',
        'Life\'s better when you\'re doing something active',
        'Energetic type seeking equally motivated people',
      ];

      const middles = [
        `Love ${interestList}`,
        `Big into ${interestList}`,
        `${interestList} keep me busy`,
        `${interestList} are basically my life`,
        `Addicted to ${interestList}`,
        `You'll always find me doing ${interestList}`,
        `${interestList} give me life`,
      ];

      const middles2 = [
        'but honestly down for most active stuff',
        'and always looking for the next thing to try',
        'plus I enjoy pushing myself and others',
        'though I\'ll try any activity at least once',
        'but love variety and mixing things up',
        'and I\'m always chasing that endorphin rush',
        'though weekends are for trying something completely new',
      ];

      const endings = [
        'Down for regular hangouts, spontaneous plans, or just getting out there and doing something',
        'Hit me up if you want to do something active - gym, sports, adventures, whatever',
        'Always down to try new activities together and keep each other motivated',
        'Looking for workout buddies, adventure partners, or just people who say yes',
        'Let\'s meet up and get active - I promise I\'ll keep it fun',
        'Need people who are game for anything and everything',
        'Want to find friends who push me to try new things and stay active',
      ];

      return `${random(intros)}! ${random(middles)}, ${random(middles2)}. ${random(endings)}!`;
    }
  },
  {
    id: 'community',
    label: '🤝 Warm & Welcoming',
    icon: '🤝',
    description: 'Creating meaningful connections',
    template: ({ age, interests }) => {
      const intros = [
        'Looking to build a solid friend group',
        'Hoping to find my people',
        'Building my tribe',
        'Creating a circle of genuine friends',
        'Keen to be part of a real community',
        'Want to find people who become like family',
        'Looking for depth, not just surface-level friendships',
        'Believer in building strong, supportive friend groups',
      ];

      const interestList = formatInterests(interests, 3);
      const middles = [
        `Really into ${interestList}`,
        `Love ${interestList}`,
        `${interestList} are important to me`,
        `${interestList} bring me joy`,
        `${interestList} are my passion`,
        `You'll usually find me at ${interestList}`,
        `Big believer in ${interestList}`,
      ];

      const middles2 = [
        'and believe in building real, lasting friendships',
        'plus I value authenticity and good energy',
        'and think the best connections happen naturally',
        'but what matters most is the people I share it with',
        'though I\'m always open to experiencing new things with friends',
        'and care deeply about the people in my life',
        'but friendship is what really makes life meaningful to me',
      ];

      const endings = [
        'Let\'s create something meaningful together - whether that\'s events, hangouts, or just a solid friend group',
        'Looking for genuine connections with people who want the same',
        'Want to build a real community of friends who actually show up for each other',
        'Hoping to meet people who value loyalty and authenticity',
        'Let\'s build friendships that actually last and mean something',
        'Looking for my people - the ones who become chosen family',
        'Want to create a tribe where everyone genuinely cares about each other',
      ];

      return `${random(intros)}. ${random(middles)}, ${random(middles2)}. ${random(endings)}!`;
    }
  }
];

// Helper to pick random element from array
function random<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Format interests into a natural-sounding list
 */
function formatInterests(interests: string[], maxCount: number = 3): string {
  const selected = interests.slice(0, maxCount);

  if (selected.length === 0) return 'trying new things';
  if (selected.length === 1) return selected[0].toLowerCase();
  if (selected.length === 2) return `${selected[0].toLowerCase()} and ${selected[1].toLowerCase()}`;

  const lastInterest = selected[selected.length - 1];
  const otherInterests = selected.slice(0, -1);
  return `${otherInterests.map(i => i.toLowerCase()).join(', ')}, and ${lastInterest.toLowerCase()}`;
}

/**
 * Generate a bio based on personality type and user data
 */
export function generateBio(
  personalityType: PersonalityType,
  params: BioParams
): string {
  const template = PERSONALITY_TEMPLATES.find(t => t.id === personalityType);
  if (!template) {
    throw new Error(`Unknown personality type: ${personalityType}`);
  }

  return template.template(params);
}

/**
 * Get all available personality templates
 */
export function getPersonalityTemplates(): PersonalityTemplate[] {
  return PERSONALITY_TEMPLATES;
}

/**
 * Get a specific personality template
 */
export function getPersonalityTemplate(type: PersonalityType): PersonalityTemplate | undefined {
  return PERSONALITY_TEMPLATES.find(t => t.id === type);
}

/**
 * Remove sensitive location data from bio text
 * CRITICAL SAFEGUARDING: Strips ALL street addresses, door numbers, and postcodes
 * Only allows city/town names - NEVER specific addresses
 */
export function sanitizeBioLocation(bio: string): string {
  if (!bio) return bio;

  let sanitized = bio;

  // Step 1: Remove UK postcodes FIRST (must be done before other replacements)
  // Matches: SW17 0QN, W1A 1AA, EC1A 1BB, etc.
  // Pattern: 1-2 letters, 1-2 digits, optional letter, space, 1 digit, 2 letters
  sanitized = sanitized.replace(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/gi, '');

  // Step 2: Remove US/Canadian zip codes
  sanitized = sanitized.replace(/\b\d{5}(-\d{4})?\b/g, '');

  // Step 3: Remove street addresses with door/building numbers
  // Matches patterns like: "86A Coverton Rd", "123 Main Street", "5B Park Lane"
  // Pattern: digit(s), optional letter, space, any text, street suffix
  sanitized = sanitized.replace(/\b\d+[A-Z]?\s+[A-Za-z\s]+?\s*(Road|Rd|Street|St|Avenue|Ave|Lane|Ln|Drive|Dr|Way|Close|Court|Ct|Place|Pl|Terrace|Tce|Crescent|Cres|Boulevard|Blvd|Square|Sq|Hill|Grove|Gardens|Park|Row|Walk|Mews|Green)\b/gi, '');

  // Step 4: Remove any standalone street suffixes that might remain
  // This catches cases like "living in Coverton Rd"
  sanitized = sanitized.replace(/\b[A-Za-z]+\s+(Road|Rd|Street|St|Avenue|Ave|Lane|Ln|Drive|Dr|Way|Close|Court|Ct|Place|Pl|Terrace|Tce|Crescent|Cres|Boulevard|Blvd|Square|Sq|Hill|Grove|Gardens|Park|Row|Walk|Mews|Green)\b/gi, '');

  // Step 5: Remove any remaining building/door numbers followed by text before a comma
  // Catches "86A anything," patterns
  sanitized = sanitized.replace(/\b\d+[A-Z]?\s+[^,]+?,/gi, '');

  // Step 6: Clean up formatting issues
  // Remove multiple spaces
  sanitized = sanitized.replace(/\s+/g, ' ');
  // Remove multiple commas or comma-space patterns
  sanitized = sanitized.replace(/,\s*,+/g, ',');
  // Remove space before comma
  sanitized = sanitized.replace(/\s+,/g, ',');
  // Clean up "in , " or "in  " patterns - replace with just the city that follows
  sanitized = sanitized.replace(/\b(in|at|from|living in|based in|based at)\s*,\s*/gi, '$1 ');
  // Remove comma at start of text
  sanitized = sanitized.replace(/^,\s*/, '');
  // Remove trailing commas
  sanitized = sanitized.replace(/,\s*$/, '');

  return sanitized.trim();
}
